/**
 * Redux async actions (src/store/actions/dashboardActions.js).
 *
 * This module defines Redux Toolkit thunks (createAsyncThunk) that call backend endpoints via the shared axios client.
 * Slices consume the fulfilled payloads in `extraReducers` to update the UI state.
 */

// src/store/actions/dashboardActions.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../helpers/axiosconfig";

const DASHBOARD_ERROR_CODES = {
  401: "ADMIN_DASHBOARD_UNAUTHENTICATED",
  403: "ADMIN_DASHBOARD_FORBIDDEN",
  404: "ADMIN_DASHBOARD_NOT_FOUND",
  409: "ADMIN_DASHBOARD_CONFLICT",
};

const hasConfirmedDashboardPayload = (payload) =>
  Boolean(
    payload &&
      payload.success === true &&
      payload.data &&
      typeof payload.data === "object" &&
      !Array.isArray(payload.data) &&
      payload.data.overview &&
      typeof payload.data.overview === "object" &&
      !Array.isArray(payload.data.overview)
  );

export const normalizeDashboardError = (error) => {
  const status = Number(error?.response?.status) || null;
  const responseData = error?.response?.data;
  const responseCode =
    typeof responseData?.code === "string" ? responseData.code : null;

  return {
    status,
    code: responseCode || DASHBOARD_ERROR_CODES[status] || "ADMIN_DASHBOARD_UNAVAILABLE",
  };
};

/**
 * Async thunk: getAdminDashboardStats.
 *
 * Wraps an HTTP request (via the shared axios client) into a dispatchable Redux action.
 */
export const getAdminDashboardStats = createAsyncThunk(
  "dashboard/getAdminDashboardStats",
  async (input = 30, { rejectWithValue }) => {
    try {
      const params =
        typeof input === "object" && input !== null
          ? input
          : { timeRange: input };
      const { timeRange = 30, scope, forceRefresh = false } = params;
      const response = await axios.get(`admin/dashboard/stats`, {
        params: {
          timeRange,
          ...(scope ? { scope } : {}),
          ...(forceRefresh ? { refresh: '1' } : {}),
        }
      });
      if (!hasConfirmedDashboardPayload(response.data)) {
        return rejectWithValue({
          status: Number(response.status) || null,
          code: "ADMIN_DASHBOARD_INVALID_RESPONSE",
        });
      }

      return response.data;
    } catch (error) {
      console.error("Error fetching admin dashboard stats:", error.response?.data || error.message);
      return rejectWithValue(normalizeDashboardError(error));
    }
  }
);
